import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { ContinueButton } from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import { OpenSans } from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import { gsap } from "gsap";
import { ParticleContainer, Sprite, Text, Texture } from "pixi.js";

import { BackgroundEvents, EventsManager } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../../abstracts/modules/AbstractView";
import { AbstractWinScreen } from "../../../../abstracts/modules/screens/AbstractWinScreen";
import { LevelName, TimelineLabels, WinType } from "../../../../abstracts/types/enums";
import { ITotalWinScreenData } from "../../../../abstracts/types/interfaces";
import { fitDisplayObjectToWidth } from "../../../../abstracts/utils/GraphicUtils";
import {
    fadeAndStopSound,
    fadeInBackgroundMusic,
    fadeOutBackgroundMusic,
    fadeSound,
    playNextBackgroundMusic,
    playSound,
    startAmbSounds,
    stopAmbSounds,
    stopBackgroundMusic,
    stopSound,
} from "../../../../abstracts/utils/SoundUtils";
import { DDD } from "../../../DDD";
import { DDDFontsNames, DDDSoundNames, DDDSpineNames } from "../../../assetsLoader/DDDAssetsNames";
import { maxWinLevel, winFontSize, winLevels } from "../../../config/DDDConfig";
import { respinMusic } from "../../Sound/DDDSoundController";
import { getBigWinTextAnimationConfig } from "../DDDWinTextPositionConfig";

export class DDDTotalWinScreen extends AbstractWinScreen {
    protected currentValue: number = 0;
    protected currentSpine: AbstractSpine;
    protected waysWinText: Text;
    protected spacingBetweenWinAndWays: number;
    protected maxWinMultiplier: AbstractBitmapText;

    private particleContainer: ParticleContainer;

    constructor(parentContainer: AbstractView) {
        super(`DDDTotalWinScreen`, parentContainer);
    }

    protected get totalWinTextDefaultYPosition(): number {
        return -40;
    }

    protected get waysWinTextDefaultYPosition(): number {
        return 72;
    }

    protected init(): void {
        super.init();

        this.spacingBetweenWinAndWays = 40;

        this.totalWinText = new AbstractBitmapText({
            text: `0`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: winFontSize,
            },
        });
        this.totalWinText.name = `TotalWinText`;
        this.totalWinText.alpha = 0;

        this.waysWinText = new Text(``, {
            fontFamily: OpenSans.FAMILY,
            fontSize: `48px`,
            fontWeight: `bold`,
            stroke: `#000000`,
            strokeThickness: 2,
            dropShadow: true,
            dropShadowAlpha: 0.6,
            // dropShadowAngle?: number;
            dropShadowBlur: 1,
            dropShadowColor: 0x000000,
            dropShadowDistance: 2,
            fill: `#ffffff`,
            letterSpacing: 1,
            lineJoin: `round`,
        });
        this.waysWinText.alpha = 0.88;
        this.waysWinText.name = `waysWinText`;
        this.waysWinText.y = this.waysWinTextDefaultYPosition;

        this.activeSpines = [];
        for (let i = 0; i < 2; i++) {
            const bigWinSpine = new AbstractSpine(
                i === 1 ? DDDSpineNames.MAX_WIN : DDDSpineNames.BIG_WIN
            );
            bigWinSpine.name = i === 1 ? `MaxCapSpine` : `BigWinSpine`;
            bigWinSpine.scale.set(0.535);
            this.activeSpines.push(bigWinSpine);
        }

        this.particleContainer = new ParticleContainer();

        this.particleContainer.x = -80;
        this.particleContainer.y = 80;

        this.winTextBackground = new Sprite(Texture.from(`DDD/winNumbersBG`));
        this.winTextBackground.name = `winBg`;
        this.winTextBackground.anchor.set(0.5);
        this.winTextBackground.y = 24;
        this.winTextBackground.alpha = this.totalWinText.alpha;

        this.maxWinMultiplier = new AbstractBitmapText({
            text: `19349X`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: winFontSize / 2,
            },
        });
        this.maxWinMultiplier.name = `maxWinMultiplier`;
        this.maxWinMultiplier.y = 50;
    }

    protected startLowerWinCounterAnimation(
        winAmount: number,
        animationDuration: number,
        winType: WinType,
        timeline: gsap.core.Timeline,
        waysWon: number = -1,
        onStart: Function | null = null,
        onUpdate: Function | null = null,
        onComplete: Function | null = null,
        callback: Function | null = null
    ) {
        this.currentValue = winAmount;

        this.totalWinText.cacheAsBitmap = false;

        const isLow = winType === WinType.TINY || winType === WinType.SMALL;
        const winObj: { value: number } = {
            value: isLow ? winAmount : 0,
        };

        const onCounterStart = () => {
            this.selfAddToParent();
            this.totalWinText.scale.set(0.5);
            this.totalWinText.alpha = 0.88;

            this.totalWinText.y = this.totalWinTextDefaultYPosition;

            if (waysWon <= 0) {
                this.removeChild(this.waysWinText);
            } else {
                this.totalWinText.y = this.totalWinTextDefaultYPosition;

                const waysWonString =
                    waysWon === 1
                        ? DDD.apiPlugin.translations.translate(`Way Won`)
                        : DDD.apiPlugin.translations.translate(`Ways Won`);

                this.waysWinText.text = `${waysWon} ${waysWonString}`.toUpperCase();
                this.waysWinText.x = -this.waysWinText.width / 2;
                this.addChild(this.waysWinText);
            }

            onStart !== null && onStart();

            callback !== null && callback();
        };
        const onCounterUpdate = () => {
            this.totalWinText.updateValue(winObj.value.toFixed(2).toString());

            onUpdate !== null && onUpdate();

            this.checkWinTextWidth();

            this.winTextBackground.alpha = this.totalWinText.alpha;
        };

        const onCounterComplete = () => {
            onComplete !== null && onComplete();
        };

        const counterAnimation = gsap.to(winObj, {
            value: winAmount,
            duration: animationDuration,
            ease: `none`,
            onStart: onCounterStart,
            onUpdate: onCounterUpdate,
            onComplete: onCounterComplete,
        });

        timeline.add(counterAnimation, TimelineLabels.FINISH_SYMBOLS_WIN_ANIMATION);

        timeline.addLabel(TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        timeline.addLabel(
            TimelineLabels.TEXT_COUNT_COMPLETE,
            TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT
        );

        const sharpenFont = () => {
            this.totalWinText.cacheAsBitmap = true;
            // @ts-ignore
            this.totalWinText.filters = null;
        };
        timeline.call(sharpenFont, undefined, `>`);

        const highlightAnimation = this.getHighlightWinTextAnimation();

        timeline.add(highlightAnimation, `${TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT}`);
    }

    protected getHighlightWinTextAnimation(): gsap.core.Tween {
        return gsap.delayedCall(0.5, () => {});
    }

    protected onLowerWinsStart(): void {
        this.addChildAt(this.winTextBackground, 0);
        this.addChild(this.totalWinText);
    }

    public override handleTinyWins(data: ITotalWinScreenData): void {
        super.handleTinyWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: DDDSoundNames.WIN_TINY });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline,
            data.waysWon!,
            onStart,
            onUpdate,
            null,
            data.callback
        );
    }

    public override handleSmallWins(data: ITotalWinScreenData): void {
        super.handleSmallWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: DDDSoundNames.WIN_SMALL });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline!,
            data.waysWon!,
            onStart,
            onUpdate,
            null,
            data.callback
        );
    }

    public override handleMediumWins(data: ITotalWinScreenData) {
        super.handleMediumWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: DDDSoundNames.WIN_COUNTUP });
            gsap.delayedCall(data.animationDuration!, () => {
                fadeSound({ name: DDDSoundNames.WIN_COUNTUP, from: 1, to: 0, duration: 0.5 });
            });
        };

        const onComplete = () => {
            playSound({ name: DDDSoundNames.WIN_MEDIUM });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline,
            data.waysWon!,
            onStart,
            onUpdate,
            onComplete,
            data.callback
        );
    }

    public override handleLargeWins(data: ITotalWinScreenData) {
        super.handleLargeWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: DDDSoundNames.WIN_COUNTUP });
            gsap.delayedCall(data.animationDuration!, () => {
                fadeSound({ name: DDDSoundNames.WIN_COUNTUP, from: 1, to: 0, duration: 0.5 });
            });
        };

        const onComplete = () => {
            playSound({ name: DDDSoundNames.WIN_LARGE });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline!,
            data.waysWon!,
            onStart,
            onUpdate,
            onComplete,
            data.callback
        );
    }

    public override handleBigWins(data: ITotalWinScreenData): void {
        super.handleBigWins(data);

        const levelNumber: number = +data.level!.name.replace(/^\D+/g, ``);

        const highlightAnimation = gsap.to({}, { duration: 0 });

        let initialValue =
            data.level?.name === LevelName.BIG_WIN_1 ? 0 : data.betAmount! * data.level?.min!;

        let finalValue =
            data.winAmount! < data.betAmount! * data.level?.max!
                ? data.winAmount!
                : data.betAmount! * data.level?.max!;

        const winObj: { value: number } = {
            value: initialValue,
        };

        this.totalWinText.y = 240;

        if (
            !(
                data.level?.name !== LevelName.BIG_WIN_1 &&
                finalValue <= Math.round(data.level!.min * data.betAmount! * 1.1)
            )
        ) {
            const nextLevelIndex = winLevels.indexOf(data.level!) + 1;

            if (nextLevelIndex < winLevels.length) {
                const nextLevel = winLevels[nextLevelIndex];
                if (nextLevel.winType !== WinType.MAX_CAP) {
                    if (
                        nextLevel === data.finalLevel! &&
                        data.winAmount! <= Math.round(nextLevel.min * data.betAmount! * 1.1)
                    ) {
                        finalValue = data.winAmount!;
                    }
                } else if (
                    nextLevel === data.finalLevel! &&
                    nextLevel.winType === WinType.MAX_CAP
                ) {
                    finalValue = maxWinLevel.min * 0.9 * data.betAmount!;
                }
            }

            this.currentValue = finalValue;

            const positionConfig = getBigWinTextAnimationConfig(levelNumber);

            const counterAnimation = gsap.to(winObj, {
                value: finalValue,
                duration: data.level?.animationDuration!,
                ease: `none`,
                onStart: () => {
                    this.selfAddToParent();

                    this.removeChild(this.winTextBackground);

                    if (data.level?.name === LevelName.BIG_WIN_1) {
                        this.currentSpine = this.activeSpines[0];
                        this.currentSpine.reset();
                        this.addChild(this.currentSpine);

                        this.currentSpine.getSpineTween(`level_${levelNumber}`);

                        fadeOutBackgroundMusic(0.2);

                        respinMusic.isPlaying &&
                            fadeAndStopSound({
                                name: DDDSoundNames.XNUDGE_RESPIN_MUSIC,
                                from: 1,
                                to: 0,
                                duration: 0.2,
                            });
                    } else {
                        this.currentSpine.state.setAnimation(0, `level_${levelNumber}`, false);
                    }

                    this.totalWinText.alpha = 0.88; // ? check it again when big win spines are ready
                    this.addChild(this.totalWinText);

                    // this.winTextBackground.alpha = this.totalWinText.alpha;

                    if (this.currentSoundName !== ``) {
                        fadeAndStopSound({
                            name: this.currentSoundName,
                            from: 1,
                            to: 0,
                            duration: 0.01,
                        });
                    }

                    this.currentSoundName = `bigWin${levelNumber}`;
                    playSound({ name: this.currentSoundName });

                    const callback = data.callback!;
                    callback !== null && callback();

                    if (data.level!.name !== LevelName.BIG_WIN_1) {
                        playSound({ name: DDDSoundNames.BIG_WIN_LEVEL_CHANGE });
                    }
                },
                onUpdate: () => {
                    this.totalWinText.updateValue(winObj.value.toFixed(2).toString());

                    this.checkWinTextWidth();
                },
                onComplete: () => {
                    if (data.level === data.finalLevel) {
                        playSound({ name: DDDSoundNames.BIG_WIN_END });
                    }
                },
            });

            data.timeline.add(counterAnimation, data.level?.name!);

            const bounceTextOnLevelChangeAnimation = gsap.fromTo(
                this.totalWinText,
                {
                    pixi: { scale: positionConfig.initialScale - 0.1 },
                },
                {
                    pixi: { scale: positionConfig.initialScale },
                    duration: 0.5,
                    ease: `back.out(2.4)`,
                    onUpdate: () => {
                        this.checkWinTextWidth();
                    },
                }
            );

            data.timeline.add(bounceTextOnLevelChangeAnimation, data.level?.name!);

            this.addChildAt(this.winTextBackground, 0);
            this.addChild(this.totalWinText);
        }

        if (data.level === data.finalLevel) {
            data.timeline.addLabel(TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

            data.timeline.add(highlightAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

            data.timeline.addLabel(TimelineLabels.TEXT_COUNT_COMPLETE);
        }

        if (data.level?.name === LevelName.BIG_WIN_1) {
            playSound({ name: DDDSoundNames.BIG_WIN_INTRO });
        }
    }

    public onBigWinsComplete(data: ITotalWinScreenData) {
        super.onBigWinsComplete(data);

        const bigWinsEndTimeline = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                this.totalWinText.y = this.totalWinTextDefaultYPosition;
                this.removeChild(this.currentSpine);

                fadeSound({
                    name: this.currentSoundName,
                    from: 1,
                    to: 0,
                    duration: 0.01,
                });

                if (
                    DDD.model.gameData.mode === AbstractGameMode.RESPIN &&
                    DDD.model.gameData.nextMode === AbstractGameMode.RESPIN
                ) {
                    playSound({
                        name: DDDSoundNames.XNUDGE_RESPIN_MUSIC,
                    });
                } else if (
                    DDD.model.gameData.mode === AbstractGameMode.NORMAL &&
                    (DDD.model.gameData.nextMode === AbstractGameMode.RESPIN ||
                        DDD.model.gameData.nextMode === AbstractGameMode.NORMAL)
                ) {
                    fadeInBackgroundMusic(1);
                } else if (
                    DDD.model.gameData.mode === AbstractGameMode.FREESPIN ||
                    DDD.model.gameData.mode === AbstractGameMode.SUPER_FREESPIN
                ) {
                    fadeInBackgroundMusic(1);
                }
            },
            onComplete: () => {
                const callback = data.callback!;
                callback !== null && callback();
            },
        });

        const moveTotalWinToCenterAnimation = gsap.to(this.totalWinText, {
            // y: 33,
            pixi: { scale: 0.5 },
            duration: 0.4,
            ease: `back.out(2)`,
            onStart: () => {
                this.winTextBackground.alpha = this.totalWinText.alpha;
                this.addChildAt(this.winTextBackground, 0);

                const callback = data.callback!;
                callback !== null && callback();

                const waysWonString =
                    data.waysWon === 1
                        ? DDD.apiPlugin.translations.translate(`Way Won`)
                        : DDD.apiPlugin.translations.translate(`Ways Won`);

                this.waysWinText.text = `${data.waysWon} ${waysWonString}`.toUpperCase();
                this.waysWinText.x = -this.waysWinText.width / 2;

                this.addChild(this.waysWinText);
            },
            onUpdate: () => {
                this.checkWinTextWidth();

                this.winTextBackground.alpha = this.totalWinText.alpha;
            },
            onComplete: () => {
                const callback = data.callback!;
                callback !== null && callback();
            },
        });

        const showWaysTextAnimation = gsap.fromTo(
            this.waysWinText,
            { alpha: 0 },
            {
                alpha: 0.88,
                duration: moveTotalWinToCenterAnimation.totalDuration() / 2,
            }
        );

        const hideCurrentSpineAnimation = gsap.to(this.currentSpine, {
            duration: moveTotalWinToCenterAnimation.totalDuration(),
            alpha: 0,
        });

        bigWinsEndTimeline.add(hideCurrentSpineAnimation, 0);
        bigWinsEndTimeline.add(moveTotalWinToCenterAnimation, 0);
        bigWinsEndTimeline.add(
            showWaysTextAnimation,
            moveTotalWinToCenterAnimation.totalDuration() / 2
        );

        data.timeline.add(bigWinsEndTimeline, `${TimelineLabels.ON_BIG_WINS_COMPLETE_START}+=0`);

        data.timeline.addLabel(TimelineLabels.ON_BIG_WINS_COMPLETE_FINISH, `>+=1.5`);
    }

    public override handleMaxCap(data: ITotalWinScreenData): void {
        super.handleMaxCap(data);

        const winObj: { value: number } = {
            value: maxWinLevel.min * 0.9 * data.betAmount!,
        };

        const highlightAnimation = this.getHighlightWinTextAnimation();

        const counterAnimation = gsap.to(winObj, {
            value: data.winAmount,
            duration: data.level?.animationDuration!,
            ease: `none`,
            onStart: () => {
                stopAmbSounds();
                stopBackgroundMusic();

                this.addChild(this.activeSpines[1]);
                this.removeChild(this.currentSpine);
                this.currentSpine = this.activeSpines[1];

                this.currentSpine.getSpineTween(`maxwin`);

                this.addChild(this.totalWinText);

                stopSound({
                    name: DDDSoundNames.BIG_WIN_5,
                });

                const callback = data.callback!;
                callback !== null && callback();

                this.removeChild(this.waysWinText);

                playSound({ name: DDDSoundNames.MAX_CAP_COUNTUP });
            },
            onUpdate: () => {
                this.totalWinText.updateValue(winObj.value.toFixed(2).toString());
                this.winTextBackground.alpha = this.totalWinText.alpha;

                this.checkWinTextWidth();
            },
        });

        data.timeline.add(counterAnimation, data.level?.name!);

        const scaleUpTextAnimation = gsap.to(this.totalWinText, {
            pixi: { scale: 0.5 },
            ease: `none`,
            duration: counterAnimation.duration(),
            onUpdate: () => {
                this.checkWinTextWidth();
            },
        });

        data.timeline.add(scaleUpTextAnimation, data.level?.name!);

        data.timeline.addLabel(TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);
        data.timeline.add(highlightAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        const moveTextAboveButtonAnimation = gsap.to(this.totalWinText, {
            y: -230,
            duration: 0.2,
        });

        data.timeline.add(moveTextAboveButtonAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        const sharpenFont = () => {
            this.totalWinText.cacheAsBitmap = true;
            // @ts-ignore
            this.totalWinText.filters = null;
        };
        data.timeline.call(sharpenFont, undefined, `>`);

        data.timeline.addLabel(TimelineLabels.SHOW_CONTINUE_BUTTON);

        data.timeline.call(
            () => {
                playSound({ name: DDDSoundNames.MAX_CAP_LOOP, repeat: 10000 });

                this.currentSpine.state.setAnimation(0, `loop`, true).mixDuration = 0.01;

                this.continueButton = new ContinueButton();

                this.continueButton.enable(true);
                this.continueButton.x = -this.continueButton.width / 2;
                this.continueButton.y = 188;

                const continueBtnCallback = () => {
                    this.totalWinText.y = this.totalWinTextDefaultYPosition;
                    // if (
                    //     DDD.model.hasXGod ||
                    //     (DDD.model.gameData.brokeTheBank &&
                    //         DDD.model.gameData.betWayWins.length === 0)
                    // ) {
                    // }

                    if (DDD.model.gameData.brokeTheBank) {
                        this.totalWinText.y = 7;
                        this.maxWinMultiplier.y = 200;
                    }

                    this.addChildAt(this.winTextBackground, 0);

                    this.removeChild(this.continueButton);
                    // this.removeChild(this.maxWinMultiplier);

                    this.continueButton.destroy();

                    data.timeline.resume();

                    fadeAndStopSound({
                        name: DDDSoundNames.MAX_CAP_LOOP,
                        from: 1,
                        to: 0,
                        duration: 0.5,
                    });

                    stopBackgroundMusic();
                    playNextBackgroundMusic();
                    startAmbSounds();
                    EventsManager.trigger(BackgroundEvents.SHOW_NEXT_BACKGROUND);
                    playSound({ name: DDDSoundNames.CONTINUE_BUTTON });
                    playSound({ name: DDDSoundNames.BIG_WIN_END });

                    const isTotalWinInMultipleSpins =
                        DDD.model.gameData.mode !== AbstractGameMode.NORMAL;

                    SlotGame.keypadPlugin.setWin(data.winAmount!, isTotalWinInMultipleSpins, false);
                };

                this.continueButton.addClickCallback(continueBtnCallback);

                gsap.delayedCall(0.1, () => {
                    this.addChild(this.continueButton);
                });

                this.maxWinMultiplier.alpha = this.totalWinText.alpha;
                this.maxWinMultiplier.y = 50;
                this.addChild(this.maxWinMultiplier);

                data.timeline.pause();
            },
            undefined,
            TimelineLabels.SHOW_CONTINUE_BUTTON
        );

        data.timeline.addLabel(
            TimelineLabels.TEXT_COUNT_COMPLETE,
            `${TimelineLabels.SHOW_CONTINUE_BUTTON}-=0.02`
        );
    }

    public onMaxCapComplete(data: ITotalWinScreenData) {
        super.onMaxCapComplete(data);

        data.timeline.addLabel(
            TimelineLabels.ON_MAX_CAP_COMPLETE_START,
            TimelineLabels.SHOW_CONTINUE_BUTTON
        );

        data.timeline.addLabel(
            TimelineLabels.SHOW_NEXT_BACKGROUND,
            TimelineLabels.SHOW_CONTINUE_BUTTON
        );

        const showFinalTextTimeline = gsap.timeline({});

        const moveTotalWinToCenterAnimation = gsap.to(this.totalWinText, {
            pixi: { scale: 0.5 },
            duration: 0.5,
            ease: `back.out(2)`,
            onUpdate: () => {
                this.checkWinTextWidth();
            },
            onComplete: () => {
                const callback = data.callback!;
                callback !== null && callback();

                this.currentSoundName = ``;
            },
        });

        const alphaObj: { value: number } = { value: 1 };

        const hideMaxCapSpineAnimation = gsap.to(alphaObj, {
            duration: moveTotalWinToCenterAnimation.totalDuration(),
            value: 0,
            onUpdate: () => {
                this.activeSpines[0].alpha = alphaObj.value;
                this.activeSpines[1].alpha = alphaObj.value;
            },
            onComplete: () => {
                this.activeSpines.forEach((spine) => {
                    this.removeChild(spine);
                    spine.reset();
                });
            },
        });

        showFinalTextTimeline.add(moveTotalWinToCenterAnimation, 0);
        showFinalTextTimeline.add(hideMaxCapSpineAnimation, 0);

        data.timeline.add(showFinalTextTimeline, TimelineLabels.ON_MAX_CAP_COMPLETE_START);
        data.timeline.addLabel(
            TimelineLabels.ON_MAX_CAP_COMPLETE_FINISH,
            `${TimelineLabels.ON_MAX_CAP_COMPLETE_START}+=${showFinalTextTimeline.duration() + 1.5}`
        );
    }

    public finish(data: ITotalWinScreenData) {
        gsap.to(this.totalWinText, {
            duration: data.finishTextAnimationDuration,
            alpha: 0,

            onStart: () => {
                if (data.waysWon! <= 0 || data.winType === WinType.MAX_CAP) {
                    this.removeChild(this.waysWinText);
                }
            },
            onUpdate: () => {
                if (data.waysWon! > 0) {
                    this.waysWinText.alpha = this.totalWinText.alpha;
                }
                this.winTextBackground.alpha = this.totalWinText.alpha;

                if (
                    DDD.model.hasXGod ||
                    (DDD.model.gameData.brokeTheBank && DDD.model.gameData.betWayWins.length === 0)
                ) {
                    this.maxWinMultiplier.alpha = this.totalWinText.alpha;
                }
            },
            onComplete: () => {
                this.removeChild(this.totalWinText);
                this.removeChild(this.winTextBackground);
                this.removeChild(this.maxWinMultiplier);

                this.removeChild(this.waysWinText);
                this.waysWinText.alpha = 0.88;

                // @ts-ignore
                this.totalWinText.filters = null;
                this.selfRemoveFromParent();

                this.activeSpines.forEach((spine) => {
                    this.removeChild(spine);
                    spine.reset();
                });

                this.currentSoundName = ``;
            },
        });

        this.currentValue = 0;
    }

    protected checkWinTextWidth(): void {
        fitDisplayObjectToWidth(this.totalWinText, 700);
    }
}
